<template>
  <div class="lead-form">
    <!-- Sem campos configurados -->
    <div v-if="fields.length === 0" class="lead-muted">
      Nenhum campo configurado. Defina os campos em
      <strong>Controle de acesso → Configurar Formulário de Coleta de Lead</strong>.
    </div>

    <!-- Formulário de coleta -->
    <div v-else class="lead-fields">
      <div
        v-for="field in visibleFields"
        :key="field.id || field.name"
        class="lead-field"
      >
        <label class="lead-label">
          {{ field.label || field.name }}
          <span v-if="field.required" class="lead-required">*</span>
        </label>

        <!-- Textarea -->
        <textarea
          v-if="getType(field) === 'textarea'"
          v-model="formData[field.name]"
          class="lead-input"
          rows="3"
          :placeholder="field.placeholder || ''"
        ></textarea>

        <!-- Select (seleção única) -->
        <select
          v-else-if="getType(field) === 'select'"
          v-model="formData[field.name]"
          class="lead-input lead-select"
        >
          <option value="">Selecione...</option>
          <option v-for="opt in optionsOf(field)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- Multiselect (dropdown de tags) -->
        <i-multiselect
          v-else-if="getType(field) === 'multiselect'"
          v-model="formData[field.name]"
          :options="optionsOf(field)"
          placeholder="Selecione..."
        />

        <!-- Radio (seleção única) -->
        <div v-else-if="getType(field) === 'radio'" class="lead-choices">
          <label v-for="opt in optionsOf(field)" :key="opt.value" class="lead-check">
            <input type="radio" :value="opt.value" v-model="formData[field.name]" />
            <span>{{ opt.label }}</span>
          </label>
        </div>

        <!-- Checkbox único -->
        <label v-else-if="getType(field) === 'checkbox'" class="lead-check lead-check--single">
          <input type="checkbox" v-model="formData[field.name]" />
          <span>{{ field.placeholder || 'Sim' }}</span>
        </label>

        <!-- Data / Data e hora -->
        <input
          v-else-if="getType(field) === 'date'"
          type="date"
          v-model="formData[field.name]"
          class="lead-input"
        />
        <input
          v-else-if="getType(field) === 'datetime'"
          type="datetime-local"
          v-model="formData[field.name]"
          class="lead-input"
        />

        <!-- Texto / e-mail / número / etc -->
        <input
          v-else
          :type="inputTypeOf(field)"
          v-model="formData[field.name]"
          :placeholder="field.placeholder || ''"
          class="lead-input"
        />

        <div v-if="field.help_text" class="lead-help">{{ field.help_text }}</div>
      </div>

      <button
        type="button"
        class="btn btn-primary lead-submit"
        :disabled="saving"
        @click="submit"
      >
        <i class="bx bx-save mr-1"></i>{{ saving ? 'Salvando...' : 'Registrar coleta' }}
      </button>
    </div>

    <!-- Histórico de coletas (accordion, recolhido por padrão) -->
    <div v-if="submissions.length" class="lead-history">
      <button type="button" class="lead-history-toggle" @click="historyOpen = !historyOpen">
        <span>Coletas registradas ({{ submissions.length }})</span>
        <i class="bx" :class="historyOpen ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
      </button>
      <div v-show="historyOpen" class="lead-history-list">
        <div v-for="sub in submissions" :key="sub.id" class="lead-history-item">
          <div class="lead-history-meta">
            <i class="bx bx-user-circle"></i>
            {{ sub.created_by_name || 'Operador' }}
            <span class="lead-history-date">{{ $moment(sub.created_at).format('LLL') }}</span>
          </div>
          <div class="lead-history-answers">
            <div v-for="(ans, i) in answersOf(sub)" :key="i" class="lead-answer">
              <span class="lead-answer-label">{{ ans.label }}:</span>
              <span class="lead-answer-value">{{ ans.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { CHECK_IN_MODULE } from '@/modules/check-in'
import { fetchLeadSubmissions, saveLeadSubmission } from '@/modules/check-in/service/leadForm'
import { shouldShowField } from '@/modules/check-in/utils/conditionEvaluator'
import IMultiselect from '@/components/inputs/IMultiselect.vue'

const DISPLAY_ONLY = ['disclaimer', 'instrucoes', 'fake_ticket', 'section']

export default {
  name: 'info-check-in-lead-form',
  components: { IMultiselect },
  props: {
    form: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    saving: false,
    formData: {},
    submissions: [],
    historyOpen: false
  }),
  computed: {
    currentId () {
      return this.$store.state[CHECK_IN_MODULE].showDetailId
    },
    fields () {
      const raw = (this.form && this.form.fields) || []
      return raw.filter(f => f && !DISPLAY_ONLY.includes(this.getType(f)))
    },
    visibleFields () {
      return this.fields.filter(f => shouldShowField(f, this.formData, this.fields))
    }
  },
  watch: {
    currentId () {
      this.resetFormData()
      this.loadSubmissions()
    },
    form () {
      this.resetFormData()
    }
  },
  mounted () {
    this.resetFormData()
    this.loadSubmissions()
  },
  methods: {
    getType (field) {
      return field.type ||
        (field.fieldType && field.fieldType.name) ||
        (field.field_type && field.field_type.name) ||
        (field.config && field.config.type) ||
        'text'
    },
    inputTypeOf (field) {
      const t = this.getType(field)
      if (t === 'email') return 'email'
      if (['number', 'decimal', 'percentage'].includes(t)) return 'number'
      return 'text'
    },
    optionsOf (field) {
      const raw = field.options || (field.config && field.config.options) || []
      return raw.map(o => {
        if (o && typeof o === 'object') return { label: o.label != null ? o.label : o.value, value: o.value }
        return { label: o, value: o }
      })
    },
    resetFormData () {
      const data = {}
      this.fields.forEach(f => {
        const t = this.getType(f)
        if (t === 'multiselect') data[f.name] = []
        else if (t === 'checkbox') data[f.name] = false
        else data[f.name] = ''
      })
      this.formData = data
    },
    async loadSubmissions () {
      if (!this.currentId) {
        this.submissions = []
        return
      }
      try {
        this.submissions = await fetchLeadSubmissions(this.currentId)
      } catch (e) {
        this.submissions = []
      }
    },
    answersOf (sub) {
      const data = sub && sub.data
      if (!Array.isArray(data)) return []
      return data.map(a => ({
        label: a.label || a.name,
        value: this.displayValue(a.value)
      }))
    },
    displayValue (v) {
      if (Array.isArray(v)) return v.join(', ') || '—'
      if (v === true) return 'Sim'
      if (v === false) return 'Não'
      if (v === null || v === undefined || v === '') return '—'
      return String(v)
    },
    async submit () {
      const missing = this.visibleFields.filter(f => {
        if (!f.required) return false
        const val = this.formData[f.name]
        if (Array.isArray(val)) return val.length === 0
        return val === '' || val === null || val === undefined || val === false
      })
      if (missing.length) {
        errorMessage('Preencha os campos obrigatórios: ' + missing.map(f => f.label || f.name).join(', '))
        return
      }

      const answers = this.visibleFields.map(f => ({
        name: f.name,
        label: f.label || f.name,
        value: this.formData[f.name]
      }))

      this.saving = true
      try {
        await saveLeadSubmission(this.currentId, answers)
        successMessage('Coleta de lead registrada.')
        this.resetFormData()
        await this.loadSubmissions()
      } catch (e) {
        errorMessage('Ocorreu um erro ao registrar a coleta de lead.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.lead-muted {
  font-size: 12px;
  color: #74788d;
}
.lead-field {
  margin-bottom: 14px;
}
.lead-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.lead-required {
  color: #ef4444;
}

/* Inputs no padrão da lp-padrão, com altura um pouco menor */
.lead-input {
  width: 100%;
  display: block;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 11px 14px;
  font-size: 14px;
  color: #1f2937;
  line-height: 1.3;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.lead-input:focus {
  border-color: #9ca3af;
  box-shadow: 0 0 0 1px #e5e7eb;
}
textarea.lead-input {
  resize: vertical;
  min-height: 80px;
}

/* Select — remove aparência nativa (que quebrava no mobile) e usa seta própria */
.lead-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 38px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 13px center;
  background-size: 13px;
}

.lead-choices {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
}
.lead-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  margin: 0;
  cursor: pointer;
}
.lead-check input {
  width: 16px;
  height: 16px;
}
.lead-check--single {
  padding-top: 2px;
}

.lead-help {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Botão de envio — largura total, igual ao padrão da lp-padrão */
.lead-submit {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 11px 16px;
  font-size: 14px;
  border-radius: 6px;
}

/* Multiselect — leve ajuste de altura para acompanhar os demais campos */
.lead-form >>> .ms-trigger {
  min-height: 42px;
  max-height: none;
  border-radius: 6px;
  border-color: #d1d5db;
}

/* Histórico — accordion */
.lead-history {
  margin-top: 18px;
  border-top: 1px solid #eff0f2;
  padding-top: 12px;
}
.lead-history-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #f8f9fa;
  border: 1px solid #eff0f2;
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
}
.lead-history-toggle i {
  font-size: 18px;
}
.lead-history-list {
  margin-top: 6px;
}
.lead-history-item {
  background: #f8f9fa;
  border: 1px solid #eff0f2;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;
}
.lead-history-meta {
  font-size: 11px;
  color: #74788d;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}
.lead-history-date {
  margin-left: auto;
}
.lead-answer {
  font-size: 12px;
  color: #343a40;
}
.lead-answer-label {
  font-weight: 600;
}
</style>
